import { isEmpty } from 'lodash-es'
import { isAvailableNow } from '@tictuk/item-availability'

import Home from 'mobx/Home'
import Infra from 'mobx/Infra'

import { getStoreInfo } from 'mobx/AddressManager/utils'
import { initSessionAndLoadMenu, validateAddress, convertGooglePlaceObjToServerObj, sendRequest, getECommerceDomainByEnv, createUrl } from 'utils/utils'
import loadMenu from 'utils/api/loadMenu/loadMenu'
import { initOrUpdateSession } from 'mobx/Session/utils'
import { webFlowAddress } from 'utils/api/webFlowAddress'
import { getHomeData } from '../../../utils/nextUtils'
import { sendCustomEvent } from 'utils/analytics/analytics'
import { geoCode } from 'utils/googleMapsUtils/googleMapsUtils'

import { staticImplements } from 'utils/types'

import type {
	LocalizationDependencies,
	LocalizationFlow,
	Configuration,
	OtherSavedAddress,
	SavedAddress,
	NonSavedAddress,
	StorageType
} from './types';
import type CartStore from 'mobx/Cart/store'
import { type AppParams } from 'mobx/Infra/Infra.type'
import type { NextRouter } from 'next/router'
import type { Store } from 'types/StoreContext'
import type AddressManagerV2Store from 'mobx/AddressManagerV2/store'
import type { StoreFrontResponse } from 'mobx/AddressManager/types'
import { StoreStatus } from 'mobx/AddressManager/types'
import LocalizationError, { ErrorCode } from 'mobx/AddressManager/errors'
import type CouponFlowStore from 'mobx/CouponFlow/store'
import Account from 'mobx/Account'
import Application from 'mobx/Application'
import { AxiosError } from 'axios'
import MaximumSavedAddressesError from './errors/MaximumSavedAddressesError'
import type EventStore from 'mobx/Events/stores'
import { EventKey } from 'mobx/Events/types'

@staticImplements<LocalizationDependencies>()
export default class LocalizationRepository {
	static cartStore: CartStore

	static eventStore: EventStore

	static couponFlowStore: CouponFlowStore

	static setStore: (store: Store) => void

	static router: NextRouter

	static addressManagerV2Store: AddressManagerV2Store | undefined

	static appParams: AppParams | null = null

	static async getStores() {
		const result = (await getHomeData(this.appParams, null, 'getBranchesList')) as any
		return result.msg || null
	}

	static getBrowserStorageType = (storageType: StorageType = 'local') => storageType === 'local' ? localStorage : sessionStorage

	static async getStoresOnAddress(address: NonSavedAddress) {
		const { l: lang, c: chainId } = this.appParams!

		const res = (await webFlowAddress.getBranchesList({
			formatted: address.formatted,
			lat: address.location.lat,
			lng: address.location.lng,
			chainId,
			lang,
			orderType: null,
			showLoader: false,
		})) as any

		return {
			shouldGoToFirstStoreFound: !!res.autoProximitySelection,
			stores: res.msg || [],
		}
	}

	static setEventStore(eventStore: EventStore): void {
		this.eventStore = eventStore
	}

	static setAppParams(appParams: AppParams): void {
		this.appParams = appParams
	}

	static setCartStore(cartStore: CartStore): void {
		this.cartStore = cartStore
	}

	static setCouponFlowStore(couponFlowStore: CouponFlowStore): void {
		this.couponFlowStore = couponFlowStore
	}

	static setSetStore(setStore: (store: Store) => void) {
		this.setStore = setStore
	}

	static setRouter(router: NextRouter) {
		this.router = router
	}

	static setAddressManagerV2Store(addressManagerV2Store: AddressManagerV2Store) {
		this.addressManagerV2Store = addressManagerV2Store
	}

	static save(flow: LocalizationFlow, storageType: StorageType = 'local') {
		this.getBrowserStorageType(storageType).setItem('localizationFlow', JSON.stringify(flow))
	}

	static async fetchSavedAddresses(): Promise<SavedAddress[]> {
		const authHeader = await Account.getAuthorizationHeader(Application.backendChannel)

		return sendRequest(true, createUrl(getECommerceDomainByEnv(), `/v3/users/me/addresses`), 'get', null, authHeader) as Promise<SavedAddress[]>
	}

	static async createSavedAddress(flow: LocalizationFlow): Promise<SavedAddress> {
		const authHeader = await Account.getAuthorizationHeader(Application.backendChannel)
		const body = {
			...flow.address,
			label: flow.savingAddressData?.label,
			customLabel: flow.savingAddressData?.customLabel,
			locationDetails: flow.locationDetails
		}

		try {
			const savedAddress = await sendRequest(false, createUrl(getECommerceDomainByEnv(), `/v3/users/me/addresses`), 'post', body, authHeader)
			return savedAddress as Promise<SavedAddress>
		} catch (err) {
			if (err instanceof AxiosError && err.response?.data.error?.code === 'addresses.maxLengthExceeded') {
				throw new MaximumSavedAddressesError()
			}

			throw err
		}
	}

	static updateMapStores(stores: any[]): void {
		Home.setStores(stores)
	}

	static emitAddressSavedEvent(savedAddress: SavedAddress) {
		this.eventStore?.emitEvent({ key: EventKey.AddressSaved, data: { savedAddress } })
	}

	static emitAddressFailedToSaveEvent(error: unknown) {
		this.eventStore?.emitEvent({ key: EventKey.AddressFailedToSave, data: { error } })
	}

	static emitOrderTypeFlowsUnavailable() {
		this.eventStore?.emitEvent({ key: EventKey.OrderTypeFlowsUnavailable })
	}

	static async updateSavedAddress(flow: LocalizationFlow): Promise<void> {
		const authHeader = await Account.getAuthorizationHeader(Application.backendChannel)
		const body = {
			...flow.address,
			label: flow.savingAddressData?.label,
			customLabel: (flow.savingAddressData as OtherSavedAddress).customLabel,
			locationDetails: flow.locationDetails
		}

		await sendRequest(true, createUrl(getECommerceDomainByEnv(), `/v3/users/me/addresses/${flow.savedAddressId}`), 'put', body, authHeader)
	}

	static async deleteSavedAddress(id: string): Promise<void> {
		const authHeader = await Account.getAuthorizationHeader(Application.backendChannel)

		await sendRequest(true, createUrl(getECommerceDomainByEnv(), `/v3/users/me/addresses/${id}`), 'delete', null, authHeader)
	}

	static clearCouponToApply() {
		this.couponFlowStore.clearCouponToApply()
	}

	static get(storageType: StorageType = 'local'): LocalizationFlow | null {
		if (typeof window !== 'undefined') {
			const data = this.getBrowserStorageType(storageType)?.getItem('localizationFlow')

			if (data) {
				const flow = JSON.parse(data) as LocalizationFlow

				if (flow.address?.components) {
					return flow
				}
			}
		}

		return null
	}

	static config(): Configuration | undefined {
		return this.appParams?.features?.localizationFlow
	}

	static async initSessionAndLoadMenu(storeId: string) {
		return initSessionAndLoadMenu(loadMenu)({
			isGeneratedStatically: false,
			storeId,
			router: this.router,
			setStore: this.setStore,
			Home,
			cartStore: this.cartStore,
			addressManagerV2Store: this.addressManagerV2Store,
            redirectToMenu: false
		})
	}

	static async initSession(storeId: string | undefined) {
		const orderType = this.addressManagerV2Store?.formattedOrderTypeForSession()
		return initOrUpdateSession({ storeId, refObject: { orderType } })
	}

	static async getValidateAddress(address: NonSavedAddress): Promise<any> {
		const googlePlacesObjToConvert = {
			formatted_address: address.formatted,
			geometry: { location: { lat: address.location.lat, lng: address.location.lng } },
			types: [],
			address_components: address.components,
		}

		const convertedAddressObj = convertGooglePlaceObjToServerObj(googlePlacesObjToConvert)
		const validateAddressResponse = await validateAddress(convertedAddressObj, false)

		return validateAddressResponse
	}

	static async goToLocalizedMenu(): Promise<void> {
		const menuUrl = localStorage?.getItem('menuUrl')
		if (menuUrl) {
			await this.router.push(`/menu${menuUrl}`)
		}
	}

	static getLocalizedMenuPath(): string | undefined {
		const menuUrl = localStorage?.getItem('menuUrl')
		if (menuUrl) {
			return `/menu${menuUrl}`
		}
	}

	static async getStoreDetailsById(storeId: string): Promise<StoreFrontResponse> {
		return getStoreInfo(storeId, Infra)
	}

	static async checkPickupAvailabilityByStore(store: StoreFrontResponse) {
		if (store.status !== StoreStatus.Active) {
			throw new LocalizationError('Store is Shut Down', ErrorCode.STORE_CLOSED)
		}

		if (!store.availability) {
			throw new LocalizationError('Store is missing availability', ErrorCode.STORE_CLOSED)
		}

		const storeAvailable = isAvailableNow(store.availability)

		if (!storeAvailable) {
			throw new LocalizationError('Store is closed', ErrorCode.STORE_CLOSED)
		}

		if (!store.orderTypes.peakup.enabled) {
			throw new LocalizationError(`Pickup is disabled for store ${store.id}`, ErrorCode.PICKUP_DISABLED_FOR_STORE)
		}

		if (isEmpty(store.location)) {
			throw new LocalizationError(`Pickup is disabled for store ${store.id}`, ErrorCode.STORE_CLOSED)
		}
	}

	static sendLocalizationEvent = (eventName: string, params?: any) => {
		sendCustomEvent({
			...params,
			eventName,
			category: eventName,
			page_path: window.location.pathname
		})
	}

	static async getGeoCodeFromAddressText(address: string) {
		return geoCode(
			{
				address,
				componentRestrictions: {
					country: Home.locale?.region,
				},
			},
			Home?.locale?.key
		)
	}
}
